import api from './api/axiosConfig';

/** Unwrap ApiResponse envelope when present (RULE §9). */
function unwrap(res) {
  const d = res?.data;
  if (d && typeof d.success === 'boolean' && 'data' in d) return d.data;
  return d;
}

const propertyService = {
  // Get all properties with optional filters
  getProperties: async (params = {}) => {
    try {
      const response = await api.get('/property/api/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching properties:', error);
      throw error;
    }
  },

  // Get a single property by slug or ID
  getProperty: async (identifier) => {
    try {
      const response = await api.get(`/property/api/${identifier}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching property:', error);
      throw error;
    }
  },

  // Create a new property
  createProperty: async (propertyData) => {
    try {
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(propertyData).forEach(key => {
        if (key === 'images' && Array.isArray(propertyData[key])) {
          // Handle images separately
          propertyData[key].forEach((image, index) => {
            formData.append('images', image);
          });
        } else if (key !== 'images') {
          formData.append(key, propertyData[key]);
        }
      });

      const response = await api.post('/property/api/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error creating property:', error);
      throw error;
    }
  },

  // Update a property
  updateProperty: async (identifier, propertyData) => {
    try {
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(propertyData).forEach(key => {
        if (key === 'images' && Array.isArray(propertyData[key])) {
          // Handle images separately
          propertyData[key].forEach((image, index) => {
            formData.append('images', image);
          });
        } else if (key !== 'images') {
          formData.append(key, propertyData[key]);
        }
      });

      const response = await api.put(`/property/api/${identifier}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating property:', error);
      throw error;
    }
  },

  // Delete a property
  deleteProperty: async (identifier) => {
    try {
      const response = await api.delete(`/property/api/${identifier}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting property:', error);
      throw error;
    }
  },

  // Increment property views
  incrementViews: async (identifier) => {
    try {
      const response = await api.post(`/property/api/${identifier}/increment-views/`);
      return response.data;
    } catch (error) {
      console.error('Error incrementing views:', error);
      throw error;
    }
  },

  // Get related properties
  getRelatedProperties: async (propertyId, params = {}) => {
    try {
      const response = await api.get('/property/api/', {
        params: {
          ...params,
          exclude: propertyId,
          limit: 4
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching related properties:', error);
      throw error;
    }
  },

  // Get comments for a property (public)
  getPropertyComments: async (identifier) => {
    const response = await api.get(`/property/api/${identifier}/comments/`);
    const data = unwrap(response);
    return Array.isArray(data) ? data : (data?.results ?? []);
  },

  // Create a comment (authenticated)
  createPropertyComment: async (identifier, text) => {
    const response = await api.post(`/property/api/${identifier}/comments/`, { text });
    return unwrap(response);
  },

  // Send message to property owner (authenticated)
  contactPropertyOwner: async (identifier, { message, subject }) => {
    const response = await api.post(`/property/api/${identifier}/contact-owner/`, {
      message: message || '',
      ...(subject && { subject }),
    });
    return unwrap(response);
  },
};

export default propertyService;
